import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { collection, query } from "firebase/firestore";
import { useFirestore, useFirestoreCollectionData } from "reactfire";
import * as XLSX from "xlsx";
import MainLayout from "../../layouts/main_layout/main_layout";
import {
  MAIN_THEME_COLOR,
  SECONDARY_THEME_COLOR,
} from "../../providers/theme/colors/colors";

type RegistrationRecord = {
  id?: string;
  deanery?: { id?: number; name?: string };
  parish?: { id?: number; value?: string; name?: string };
  presidentName?: string;
  animatorName?: string;
  presidentContactNumber?: string;
  animatorContactNumber?: string;
  contactNumber?: string;
  email?: string;
  totalParticipantsCount?: number | string;
  selectedEvents?: string[];
  createdAt?: string;
  isPitchBattleEntry?: boolean;
  paymentDetails?: {
    payerName?: string;
    paymentDate?: string;
    amountPaid?: number;
    screenshotUrl?: string;
    screenshotName?: string;
  };
  events?: {
    spotlight?: {
      performingEventName?: string;
      entryName?: string;
      eventDescription?: string;
      participantCount?: number;
      participants?: string[];
      teamLeadContact?: string;
    };
    jam?: {
      participantCount?: number;
      participants?: string[];
      participantContact?: string;
    };
    ecomosaic?: {
      participantCount?: number;
      participants?: string[];
      contacts?: string[];
    };
    faithvault?: {
      participantCount?: number;
      participants?: string[];
      teamLeadContact?: string;
    };
    pitchbattle?: {
      entryType?: "individual" | "group";
      ideaTitle?: string;
      participantCount?: number;
      participants?: string[];
      teamLeadContact?: string;
    };
    rhythmroots?: {
      participantCount?: number;
      participants?: string[];
      teamLeadContact?: string;
    };
  };
};

type PitchBattleRecord = {
  id?: string;
  createdAt?: string;
  updatedAt?: string;
  deanery?: { id?: number; name?: string };
  parish?: { id?: number; value?: string; name?: string };
  presidentName?: string;
  animatorName?: string;
  presidentContactNumber?: string;
  animatorContactNumber?: string;
  contactNumber?: string;
  email?: string;
  entryType?: "individual" | "group";
  participantCount?: number;
  participantNames?: string[];
  teamLeadContact?: string;
  ideaTitle?: string;
};

type ParishSummary = {
  deanery: string;
  parish: string;
  registrationCount: number;
  totalParticipants: number;
  selectedEvents: string[];
  presidents: string[];
  animators: string[];
};

const eventLabels: Record<string, string> = {
  spotlight: "SpotLight",
  jam: "JAM (Just A Minute)",
  ecomosaic: "Eco - Mosaic",
  faithvault: "The Faith Vault",
  pitchbattle: "Pitch Battle",
  rhythmroots: "Rhythm and Roots",
};

const ExcelIcon = ({ bg = "#107C41" }: { bg?: string }) => (
  <Box
    component="span"
    sx={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: 22,
      height: 22,
      borderRadius: "4px",
      backgroundColor: bg,
      color: "#ffffff",
      fontWeight: 900,
      fontSize: "12px",
      fontFamily: "Segoe UI, sans-serif",
      boxShadow: "0 2px 4px rgba(0,0,0,0.18)",
      mr: 1,
      flexShrink: 0,
    }}
  >
    X
  </Box>
);

function getParticipantCountFromRecord(record: RegistrationRecord): number {
  if (typeof record.totalParticipantsCount === "number" && !isNaN(record.totalParticipantsCount)) {
    return record.totalParticipantsCount;
  }
  if (typeof record.totalParticipantsCount === "string") {
    const parsed = parseInt(record.totalParticipantsCount, 10);
    if (!isNaN(parsed)) return parsed;
  }
  let count = 0;
  if (record.events?.spotlight?.participantCount) count += record.events.spotlight.participantCount;
  if (record.events?.jam?.participantCount) count += record.events.jam.participantCount;
  if (record.events?.ecomosaic?.participantCount) count += record.events.ecomosaic.participantCount;
  if (record.events?.faithvault?.participantCount) count += record.events.faithvault.participantCount;
  if (record.events?.pitchbattle?.participantCount) count += record.events.pitchbattle.participantCount;
  if (record.events?.rhythmroots?.participantCount) count += record.events.rhythmroots.participantCount;
  return count;
}

export default function DycRegistrationListPage() {
  const firestore = useFirestore();
  const [activeTab, setActiveTab] = useState(0);

  const mainRegistrationQuery = query(collection(firestore, "dyc_yuva_dabazo"));
  const { data: mainRegistrations } = useFirestoreCollectionData<RegistrationRecord>(
    mainRegistrationQuery,
    { idField: "id" },
  );

  const pitchBattleQuery = query(collection(firestore, "dyc_pitch_battle"));
  const { data: pitchBattleRegistrations } = useFirestoreCollectionData<PitchBattleRecord>(
    pitchBattleQuery,
    { idField: "id" },
  );

  const mainOnlyRegistrations = useMemo(
    () => (mainRegistrations || []).filter((r) => !r.isPitchBattleEntry),
    [mainRegistrations],
  );

  const combinedPitchBattleRecords = useMemo(() => {
    const list: PitchBattleRecord[] = [...(pitchBattleRegistrations || [])];
    
    // Track parish/unit names that have actual pitch battle registrations
    const registeredParishes = new Set(
      list
        .map((item) => (item.parish?.value || item.parish?.name || "").trim().toLowerCase())
        .filter(Boolean)
    );

    // Only process main registrations that are not mirrored pitch battle entries
    const mainOnly = (mainRegistrations || []).filter((r) => !r.isPitchBattleEntry);

    mainOnly.forEach((record) => {
      if (record.events?.pitchbattle) {
        const pb = record.events.pitchbattle;
        const parishKey = (record.parish?.value || record.parish?.name || "").trim().toLowerCase();

        // If this parish has already submitted an actual pitch battle registration, skip the placeholder
        if (parishKey && registeredParishes.has(parishKey)) {
          return;
        }

        // Add placeholder record to prevent showing duplicates or empty items when actual registration exists
        const key = record.id || `${record.email}-placeholder`;
        list.push({
          id: key,
          createdAt: record.createdAt,
          deanery: record.deanery,
          parish: record.parish,
          presidentName: record.presidentName,
          animatorName: record.animatorName,
          presidentContactNumber: record.presidentContactNumber || record.contactNumber,
          animatorContactNumber: record.animatorContactNumber,
          email: record.email,
          entryType: pb.entryType || "individual",
          participantCount: pb.participantCount || pb.participants?.length || 1,
          participantNames: pb.participants || [],
          teamLeadContact: pb.teamLeadContact || record.contactNumber || "",
          ideaTitle: pb.ideaTitle || "Pitch Battle",
        });

        if (parishKey) {
          registeredParishes.add(parishKey);
        }
      }
    });

    return list;
  }, [mainRegistrations, pitchBattleRegistrations]);

  const parishSummaries = useMemo<ParishSummary[]>(() => {
    const summaryMap = new Map<string, ParishSummary>();

    mainOnlyRegistrations.forEach((record) => {
      const deanery = record.deanery?.name || "-";
      const parish = record.parish?.value || record.parish?.name || "-";
      const key = `${deanery}::${parish}`;
      const existing = summaryMap.get(key);
      const participantCount = getParticipantCountFromRecord(record);

      if (existing) {
        existing.registrationCount += 1;
        existing.totalParticipants += participantCount;
        existing.selectedEvents = Array.from(
          new Set([...existing.selectedEvents, ...(record.selectedEvents || [])]),
        );
        if (record.presidentName) {
          existing.presidents = Array.from(
            new Set([...existing.presidents, record.presidentName]),
          );
        }
        if (record.animatorName) {
          existing.animators = Array.from(
            new Set([...existing.animators, record.animatorName]),
          );
        }
        return;
      }

      summaryMap.set(key, {
        deanery,
        parish,
        registrationCount: 1,
        totalParticipants: participantCount,
        selectedEvents: record.selectedEvents || [],
        presidents: record.presidentName ? [record.presidentName] : [],
        animators: record.animatorName ? [record.animatorName] : [],
      });
    });

    return Array.from(summaryMap.values()).sort((first, second) =>
      `${first.deanery}-${first.parish}`.localeCompare(`${second.deanery}-${second.parish}`),
    );
  }, [mainOnlyRegistrations]);

  const totalMainRegistrations = mainOnlyRegistrations.length;
  const totalParishes = parishSummaries.length;
  const totalParticipantsCount = mainOnlyRegistrations.reduce(
    (sum, record) => sum + getParticipantCountFromRecord(record),
    0,
  );
  const totalPitchBattleEntries = combinedPitchBattleRecords.length;

  const handleExportMainExcel = () => {
    const workbook = XLSX.utils.book_new();

    // Sheet 1: Main Registrations (with tick mark columns for event participation)
    const mainListSheetData = mainOnlyRegistrations.map((record, index) => {
      const selected = record.selectedEvents || [];
      return {
        "Sl. No": index + 1,
        Deanery: record.deanery?.name || "-",
        Parish: record.parish?.value || record.parish?.name || "-",
        "President Name": record.presidentName || "-",
        "President Contact": record.presidentContactNumber || record.contactNumber || "-",
        "Animator Name": record.animatorName || "-",
        "Animator Contact": record.animatorContactNumber || "-",
        Email: record.email || "-",
        "Total Youth Participants Count": getParticipantCountFromRecord(record),
        "SpotLight": selected.includes("spotlight") ? "✓" : "✗",
        "JAM (Just A Minute)": selected.includes("jam") ? "✓" : "✗",
        "Eco - Mosaic": selected.includes("ecomosaic") ? "✓" : "✗",
        "The Faith Vault": selected.includes("faithvault") ? "✓" : "✗",
        "Pitch Battle": selected.includes("pitchbattle") ? "✓" : "✗",
        "Rhythm and Roots": selected.includes("rhythmroots") ? "✓" : "✗",
        "Registration Date": record.createdAt || "-",
      };
    });
    XLSX.utils.book_append_sheet(
      workbook,
      XLSX.utils.json_to_sheet(mainListSheetData),
      "Main Registrations",
    );

    // Sheet 3: SpotLight Detailed Sheet
    const spotlightRows = mainOnlyRegistrations
      .filter((record) => record.selectedEvents?.includes("spotlight"))
      .map((record, index) => {
        const spotlight = record.events?.spotlight;
        const participants = spotlight?.participants || [];
        return {
          "Sl. No": index + 1,
          Deanery: record.deanery?.name || "-",
          Parish: record.parish?.value || record.parish?.name || "-",
          "Event Name": spotlight?.performingEventName || spotlight?.entryName || "-",
          "Event Description": spotlight?.eventDescription || "-",
          "Participant Count": spotlight?.participantCount || participants.length || 1,
          "Participant 1 Name": participants[0] || "-",
          "Participant 2 Name": participants[1] || "-",
          "Participant 3 Name": participants[2] || "-",
          "Participant 4 Name": participants[3] || "-",
          "Participant 5 Name": participants[4] || "-",
          "Team Lead Contact": spotlight?.teamLeadContact || record.contactNumber || "-",
          "Registration Date": record.createdAt || "-",
        };
      });
    XLSX.utils.book_append_sheet(
      workbook,
      XLSX.utils.json_to_sheet(spotlightRows),
      "SpotLight",
    );

    // Sheet 4: JAM Detailed Sheet
    const jamRows = mainOnlyRegistrations
      .filter((record) => record.selectedEvents?.includes("jam"))
      .map((record, index) => {
        const jam = record.events?.jam;
        return {
          "Sl. No": index + 1,
          Deanery: record.deanery?.name || "-",
          Parish: record.parish?.value || record.parish?.name || "-",
          "Participant Name": (jam?.participants || [])[0] || "-",
          "Contact Number": jam?.participantContact || record.contactNumber || "-",
          "Registration Date": record.createdAt || "-",
        };
      });
    XLSX.utils.book_append_sheet(
      workbook,
      XLSX.utils.json_to_sheet(jamRows),
      "JAM",
    );

    // Sheet 5: Eco - Mosaic Detailed Sheet
    const ecomosaicRows = mainOnlyRegistrations
      .filter((record) => record.selectedEvents?.includes("ecomosaic"))
      .map((record, index) => {
        const eco = record.events?.ecomosaic;
        const participants = eco?.participants || [];
        const contacts = eco?.contacts || [];
        return {
          "Sl. No": index + 1,
          Deanery: record.deanery?.name || "-",
          Parish: record.parish?.value || record.parish?.name || "-",
          "Participant 1 Name": participants[0] || "-",
          "Participant 1 Contact": contacts[0] || record.contactNumber || "-",
          "Participant 2 Name": participants[1] || "-",
          "Participant 2 Contact": contacts[1] || "-",
          "Registration Date": record.createdAt || "-",
        };
      });
    XLSX.utils.book_append_sheet(
      workbook,
      XLSX.utils.json_to_sheet(ecomosaicRows),
      "Eco-Mosaic",
    );

    // Sheet 6: The Faith Vault Detailed Sheet
    const faithvaultRows = mainOnlyRegistrations
      .filter((record) => record.selectedEvents?.includes("faithvault"))
      .map((record, index) => {
        const faith = record.events?.faithvault;
        const members = faith?.participants || [];
        return {
          "Sl. No": index + 1,
          Deanery: record.deanery?.name || "-",
          Parish: record.parish?.value || record.parish?.name || "-",
          "Member 1 Name": members[0] || "-",
          "Member 2 Name": members[1] || "-",
          "Member 3 Name": members[2] || "-",
          "Team Lead Contact": faith?.teamLeadContact || record.contactNumber || "-",
          "Registration Date": record.createdAt || "-",
        };
      });
    XLSX.utils.book_append_sheet(
      workbook,
      XLSX.utils.json_to_sheet(faithvaultRows),
      "The Faith Vault",
    );

    // Sheet 7: Pitch Battle Detailed Sheet
    const pitchBattleRows = combinedPitchBattleRecords.map((record, index) => {
      const names = record.participantNames || [];
      return {
        "Sl. No": index + 1,
        Deanery: record.deanery?.name || "-",
        Parish: record.parish?.value || record.parish?.name || "-",
        "Pitch Title": record.ideaTitle || "-",
        "Entry Type": record.entryType || "individual",
        "Number of Participants": record.participantCount || 1,
        "Participant 1 Name": names[0] || "-",
        "Participant 2 Name": names[1] || "-",
        "Participant 3 Name": names[2] || "-",
        "Participant 4 Name": names[3] || "-",
        "Participant 5 Name": names[4] || "-",
        "Team Lead Contact": record.teamLeadContact || "-",
        "Registration Date": record.createdAt || "-",
      };
    });
    XLSX.utils.book_append_sheet(
      workbook,
      XLSX.utils.json_to_sheet(pitchBattleRows),
      "Pitch Battle",
    );

    // Sheet 8: Rhythm and Roots Detailed Sheet (Each participant as a separate row, sorted by Deanery and Parish)
    const rhythmrootsParticipants: any[] = [];
    mainOnlyRegistrations
      .filter((record) => record.selectedEvents?.includes("rhythmroots"))
      .forEach((record) => {
        const rr = record.events?.rhythmroots;
        const participants = rr?.participants || [];
        const deaneryName = record.deanery?.name || "-";
        const parishName = record.parish?.value || record.parish?.name || "-";
        
        participants.forEach((name) => {
          if (name && name.trim()) {
            rhythmrootsParticipants.push({
              deanery: deaneryName,
              parish: parishName,
              name: name.trim(),
            });
          }
        });
      });

    // Sort by Deanery first, then Parish, then Participant Name
    rhythmrootsParticipants.sort((a, b) => {
      const compDeanery = a.deanery.localeCompare(b.deanery);
      if (compDeanery !== 0) return compDeanery;
      
      const compParish = a.parish.localeCompare(b.parish);
      if (compParish !== 0) return compParish;

      return a.name.localeCompare(b.name);
    });

    // Add Sl. No to the sorted list
    const rhythmrootsRows = rhythmrootsParticipants.map((p, index) => ({
      "Sl. No": index + 1,
      "Deanery": p.deanery,
      "Parish": p.parish,
      "Participant Name": p.name,
    }));

    XLSX.utils.book_append_sheet(
      workbook,
      XLSX.utils.json_to_sheet(rhythmrootsRows),
      "Rhythm and Roots",
    );

    // Sheet 8: Payment Details Sheet
    const paymentRows = mainOnlyRegistrations.map((record, index) => {
      const pay = record.paymentDetails;
      return {
        "Sl. No": index + 1,
        Deanery: record.deanery?.name || "-",
        Parish: record.parish?.value || record.parish?.name || "-",
        "Payer Name": pay?.payerName || "-",
        "Payment Date (DD/MM/YYYY)": pay?.paymentDate || "-",
        "Amount Paid (₹)": pay?.amountPaid || "-",
        "Payment Screenshot": pay?.screenshotUrl
          ? pay.screenshotName || "Image Uploaded"
          : "Not Uploaded",
        "President Name": record.presidentName || "-",
        "President Contact": record.presidentContactNumber || record.contactNumber || "-",
        "Animator Name": record.animatorName || "-",
        Email: record.email || "-",
        "Registration Date": record.createdAt || "-",
      };
    });
    XLSX.utils.book_append_sheet(
      workbook,
      XLSX.utils.json_to_sheet(paymentRows),
      "Payment Details",
    );

    XLSX.writeFile(workbook, "dyc-yuva-dabazo-registrations.xlsx");
  };

  const handleExportPitchBattleExcel = () => {
    const workbook = XLSX.utils.book_new();

    const pitchBattleRows = combinedPitchBattleRecords.map((record, index) => {
      const names = record.participantNames || [];
      return {
        "Sl. No": index + 1,
        Deanery: record.deanery?.name || "-",
        Parish: record.parish?.value || record.parish?.name || "-",
        "Pitch Title": record.ideaTitle || "-",
        "Entry Type": record.entryType || "individual",
        "Number of Participants": record.participantCount || 1,
        "Participant 1 Name": names[0] || "-",
        "Participant 2 Name": names[1] || "-",
        "Participant 3 Name": names[2] || "-",
        "Participant 4 Name": names[3] || "-",
        "Participant 5 Name": names[4] || "-",
        "Team Lead Contact": record.teamLeadContact || "-",
        "Registration Date": record.createdAt || "-",
      };
    });

    XLSX.utils.book_append_sheet(
      workbook,
      XLSX.utils.json_to_sheet(pitchBattleRows),
      "Pitch Battle Registrations",
    );

    XLSX.writeFile(workbook, "dyc-pitch-battle-registrations.xlsx");
  };

  return (
    <MainLayout>
      <Box my={{ xs: 4, md: 6 }}>
        <Stack spacing={3.5}>
          <Box
            display="flex"
            flexDirection={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", md: "center" }}
            gap={2}
          >
            <Box>
              <Typography variant="h3" fontWeight={900} color={MAIN_THEME_COLOR}>
                DYC Registration Tracker
              </Typography>
              <Typography variant="body1" color="text.secondary" mt={0.5}>
                Parish-wise summaries, registration lists, and event breakdowns.
              </Typography>
            </Box>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <Button
                variant="contained"
                onClick={handleExportMainExcel}
                sx={{
                  px: 2.5,
                  py: 1.2,
                  borderRadius: 999,
                  backgroundColor: "#107C41",
                  color: "#ffffff",
                  fontWeight: 800,
                  "&:hover": {
                    backgroundColor: "#0b5c30",
                  },
                }}
              >
                <ExcelIcon bg="#0b5c30" />
                Download Main Excel
              </Button>

              <Button
                variant="outlined"
                onClick={handleExportPitchBattleExcel}
                sx={{
                  px: 2.5,
                  py: 1.2,
                  borderRadius: 999,
                  borderColor: "#107C41",
                  color: "#107C41",
                  fontWeight: 800,
                  "&:hover": {
                    borderColor: "#0b5c30",
                    backgroundColor: "rgba(16, 124, 65, 0.08)",
                  },
                }}
              >
                <ExcelIcon bg="#107C41" />
                Download Pitch Battle Excel
              </Button>
            </Stack>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Card sx={{ borderRadius: 3, border: `1px solid ${MAIN_THEME_COLOR}22` }}>
                <CardContent>
                  <Typography variant="body2" color="text.secondary" fontWeight={600}>
                    Parishes / Units Registered
                  </Typography>
                  <Typography variant="h4" fontWeight={900} color={MAIN_THEME_COLOR} mt={0.5}>
                    {totalParishes}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ({totalMainRegistrations} Submissions)
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card sx={{ borderRadius: 3, border: `1px solid ${MAIN_THEME_COLOR}22` }}>
                <CardContent>
                  <Typography variant="body2" color="text.secondary" fontWeight={600}>
                    Total Youth Participants
                  </Typography>
                  <Typography variant="h4" fontWeight={900} color={MAIN_THEME_COLOR} mt={0.5}>
                    {totalParticipantsCount}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Across all registered units
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card sx={{ borderRadius: 3, border: `1px solid ${SECONDARY_THEME_COLOR}44` }}>
                <CardContent>
                  <Typography variant="body2" color="text.secondary" fontWeight={600}>
                    Pitch Battle Entries
                  </Typography>
                  <Typography variant="h4" fontWeight={900} color={SECONDARY_THEME_COLOR} mt={0.5}>
                    {totalPitchBattleEntries}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Individual & Group Entries
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Card sx={{ borderRadius: 4, border: `1px solid ${MAIN_THEME_COLOR}22` }}>
            <Box sx={{ borderBottom: 1, borderColor: "divider", px: 2 }}>
              <Tabs
                value={activeTab}
                onChange={(_, newValue) => setActiveTab(newValue)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  "& .MuiTab-root": {
                    textTransform: "none",
                    fontWeight: 800,
                    fontSize: "0.95rem",
                  },
                }}
              >
                <Tab label={`Main Registrations (${mainOnlyRegistrations.length})`} />
                <Tab label={`Pitch Battle Entries (${combinedPitchBattleRecords.length})`} />
              </Tabs>
            </Box>

            <CardContent sx={{ p: 0 }}>
              {activeTab === 0 && (
                <Box>
                  <Grid
                    container
                    sx={{
                      px: 2.5,
                      py: 1.75,
                      backgroundColor: "#f6fbff",
                      borderBottom: `1px solid ${MAIN_THEME_COLOR}22`,
                    }}
                  >
                    <Grid item xs={12} sm={2}>
                      <Typography fontWeight={800}>Parish & Deanery</Typography>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Typography fontWeight={800}>President Details</Typography>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Typography fontWeight={800}>Animator Details</Typography>
                    </Grid>
                    <Grid item xs={12} sm={1.5}>
                      <Typography fontWeight={800}>Youth Count</Typography>
                    </Grid>
                    <Grid item xs={12} sm={2.5}>
                      <Typography fontWeight={800}>Selected Events</Typography>
                    </Grid>
                  </Grid>

                  {mainOnlyRegistrations.length ? (
                    mainOnlyRegistrations.map((record, idx) => (
                      <Grid
                        container
                        key={record.id || idx}
                        alignItems="center"
                        sx={{
                          px: 2.5,
                          py: 1.75,
                          borderBottom: "1px solid rgba(0,0,0,0.06)",
                          backgroundColor: idx % 2 === 0 ? "#ffffff" : "#fafcff",
                        }}
                      >
                        <Grid item xs={12} sm={2}>
                          <Typography fontWeight={700}>
                            {record.parish?.value || record.parish?.name || "-"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {record.deanery?.name || "-"}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <Typography variant="body2" fontWeight={700}>
                            {record.presidentName || "-"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Ph: {record.presidentContactNumber || record.contactNumber || "-"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {record.email || "-"}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <Typography variant="body2" fontWeight={700}>
                            {record.animatorName || "-"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Ph: {record.animatorContactNumber || "-"}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={1.5}>
                          <Typography fontWeight={800} color={MAIN_THEME_COLOR}>
                            {getParticipantCountFromRecord(record)}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={2.5}>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                            {(record.selectedEvents || []).map((ev) => (
                              <Chip
                                key={ev}
                                label={eventLabels[ev] || ev}
                                size="small"
                                sx={{ fontSize: "0.75rem" }}
                              />
                            ))}
                          </Stack>
                        </Grid>
                      </Grid>
                    ))
                  ) : (
                    <Box px={3} py={6} textAlign="center">
                      <Typography color="text.secondary">No main registration details found.</Typography>
                    </Box>
                  )}
                </Box>
              )}

              {activeTab === 1 && (
                <Box>
                  <Grid
                    container
                    sx={{
                      px: 2.5,
                      py: 1.75,
                      backgroundColor: "#fff7ef",
                      borderBottom: `1px solid ${SECONDARY_THEME_COLOR}33`,
                    }}
                  >
                    <Grid item xs={12} sm={2}>
                      <Typography fontWeight={800}>Parish & Deanery</Typography>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Typography fontWeight={800}>Pitch Title & Entry Type</Typography>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Typography fontWeight={800}>Participants & Contact</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography fontWeight={800}>Unit Lead / President Contact</Typography>
                    </Grid>
                  </Grid>

                  {combinedPitchBattleRecords.length ? (
                    combinedPitchBattleRecords.map((record, idx) => (
                      <Grid
                        container
                        key={record.id || idx}
                        alignItems="center"
                        sx={{
                          px: 2.5,
                          py: 1.75,
                          borderBottom: "1px solid rgba(0,0,0,0.06)",
                          backgroundColor: idx % 2 === 0 ? "#ffffff" : "#fffdfa",
                        }}
                      >
                        <Grid item xs={12} sm={2}>
                          <Typography fontWeight={700}>
                            {record.parish?.value || record.parish?.name || "-"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {record.deanery?.name || "-"}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <Typography variant="body2" fontWeight={800} color={MAIN_THEME_COLOR}>
                            {record.ideaTitle || "-"}
                          </Typography>
                          <Chip
                            label={record.entryType === "group" ? "Group Entry" : "Individual Entry"}
                            size="small"
                            color="secondary"
                            variant="outlined"
                            sx={{ mt: 0.5, fontSize: "0.72rem" }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <Typography variant="body2" fontWeight={700}>
                            {record.participantNames?.join(", ") || "-"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Participants: {record.participantCount || 1}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Team Lead Ph: {record.teamLeadContact || "-"}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Typography variant="body2" fontWeight={700}>
                            President: {record.presidentName || "-"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Ph: {record.presidentContactNumber || record.contactNumber || "-"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Email: {record.email || "-"}
                          </Typography>
                        </Grid>
                      </Grid>
                    ))
                  ) : (
                    <Box px={3} py={6} textAlign="center">
                      <Typography color="text.secondary">No Pitch Battle entries submitted yet.</Typography>
                    </Box>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Stack>
      </Box>
    </MainLayout>
  );
}
